import type { PostCategory } from '../../types';

/**
 * Per-category counters carried through Supercluster's map/reduce. A leaf
 * feature holds a 1 in its own column; reduce sums them up the tree, so a
 * cluster knows its own category mix without a second pass over the posts.
 */
export type CategoryTally = { progress: number; risk: number; news: number };

// How much of a cluster has to share a category before the bubble wears its
// color. Below this it stays neutral rather than claiming a majority it does
// not have.
export const CLUSTER_DOMINANCE = 0.6;

/**
 * The category a cluster should be colored by, or null when the mix has no
 * clear majority.
 *
 * Observation dots are colored by category already, so a cluster inheriting
 * its members' category uses the channel that is there rather than opening a
 * new one. Ties resolve to neither: two categories at 50/50 fall below the
 * threshold and come back null.
 */
export function dominantCategory(tally: CategoryTally): PostCategory | null {
  const total = tally.progress + tally.risk + tally.news;
  if (total === 0) return null;
  const entries: [PostCategory, number][] = [
    ['progress', tally.progress],
    ['risk', tally.risk],
    ['news', tally.news],
  ];
  let best = entries[0];
  for (const entry of entries) {
    if (entry[1] > best[1]) best = entry;
  }
  return best[1] / total >= CLUSTER_DOMINANCE ? best[0] : null;
}
